import { AutoscalingProfileOverrideDataType, AutoscalingProfileOverridesValue } from '@zerops/models/service-stack';

// Order-independent equality for the flat overrides map. Used by writeValue to skip the
// round-trip when the parent echoes our last propagated value — JSON.stringify would be
// fooled by any change in key insertion order.
export function shallowEqualRecords(
  a: AutoscalingProfileOverridesValue,
  b: AutoscalingProfileOverridesValue
): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k) || a[k] !== b[k]) return false;
  }
  return true;
}

export type ByteUnit = 'B' | 'KiB' | 'MiB' | 'GiB';
export type NsUnit = 'us' | 'ms' | 's' | 'min' | 'h';

export const BYTE_UNITS: { value: ByteUnit; label: string; factor: number }[] = [
  { value: 'B',   label: 'B',   factor: 1 },
  { value: 'KiB', label: 'KiB', factor: 1024 },
  { value: 'MiB', label: 'MiB', factor: 1024 * 1024 },
  { value: 'GiB', label: 'GiB', factor: 1024 * 1024 * 1024 }
];

export const NS_UNITS: { value: NsUnit; label: string; factor: number }[] = [
  { value: 'us',  label: 'µs',  factor: 1_000 },
  { value: 'ms',  label: 'ms',  factor: 1_000_000 },
  { value: 's',   label: 's',   factor: 1_000_000_000 },
  { value: 'min', label: 'min', factor: 60 * 1_000_000_000 },
  { value: 'h',   label: 'h',   factor: 3_600 * 1_000_000_000 }
];

const BYTE_FACTORS: Record<ByteUnit, number> = Object.fromEntries(
  BYTE_UNITS.map((u) => [ u.value, u.factor ])
) as Record<ByteUnit, number>;

const NS_FACTORS: Record<NsUnit, number> = Object.fromEntries(
  NS_UNITS.map((u) => [ u.value, u.factor ])
) as Record<NsUnit, number>;

export function isByteType(dataType: AutoscalingProfileOverrideDataType): boolean {
  return dataType === 'byte';
}

export function isNsType(dataType: AutoscalingProfileOverrideDataType): boolean {
  return dataType === 'nanosecond';
}

export function encodeByte(displayed: number, unit: ByteUnit): number {
  return displayed * BYTE_FACTORS[unit];
}

export function encodeNs(displayed: number, unit: NsUnit): number {
  return displayed * NS_FACTORS[unit];
}

export function pickLargestByteUnit(raw: number): ByteUnit {
  if (!Number.isFinite(raw) || raw === 0) return 'MiB';
  for (let i = BYTE_UNITS.length - 1; i >= 0; i--) {
    if (raw % BYTE_UNITS[i].factor === 0) return BYTE_UNITS[i].value;
  }
  return 'B';
}

export function pickLargestNsUnit(raw: number): NsUnit {
  if (!Number.isFinite(raw) || raw === 0) return 'ms';
  for (let i = NS_UNITS.length - 1; i >= 0; i--) {
    if (raw % NS_UNITS[i].factor === 0) return NS_UNITS[i].value;
  }
  return 'us';
}

export function decomposeByte(raw: number, unit?: ByteUnit): { value: number; unit: ByteUnit } {
  const u = unit ?? pickLargestByteUnit(raw);
  return { value: raw / BYTE_FACTORS[u], unit: u };
}

export function decomposeNs(raw: number, unit?: NsUnit): { value: number; unit: NsUnit } {
  const u = unit ?? pickLargestNsUnit(raw);
  return { value: raw / NS_FACTORS[u], unit: u };
}
